import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, Sparkles, ShieldCheck, Zap } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";

export default function LandingPage() {
  const [plans, setPlans] = useState([]);
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data));
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="h-16 border-b border-border bg-background/60 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto h-full flex items-center px-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-foreground text-background flex items-center justify-center font-heading font-black">
              S
            </div>
            <div className="font-heading font-black text-lg">premiumtools.store</div>
          </div>
          <div className="flex-1" />
          {user ? (
            <Button
              data-testid="header-go-account"
              onClick={() => navigate(isAdmin ? "/admin" : "/account")}
            >
              {isAdmin ? "Admin Console" : "My Account"} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <Link to="/login">
                <Button variant="ghost" data-testid="header-login">
                  Login
                </Button>
              </Link>
              <Link to="/signup">
                <Button data-testid="header-signup">Sign Up</Button>
              </Link>
            </div>
          )}
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="max-w-5xl">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-muted-foreground mb-4">
            Group-buy · Premium Marketing Tools · Shared, Verified Access
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
            Group-buy premium
            <br />
            marketing tools.
            <br />
            <span className="text-muted-foreground">Everything creators need — at a lower price.</span>
          </h1>
          <p className="text-base md:text-lg text-muted-foreground mt-6 max-w-2xl">
            Share premium marketing tool subscriptions with a verified circle. Pay via bKash, Nagad,
            or Rocket — get instant access right after admin verification.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <Link to={user ? "/account/plans" : "/signup"}>
              <Button size="lg" className="h-12 px-6" data-testid="hero-cta">
                {user ? "Browse Plans" : "Sign Up & Buy"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="#plans">
              <Button size="lg" variant="outline" className="h-12 px-6" data-testid="hero-view">
                View Plans
              </Button>
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-16">
          {[
            { icon: Sparkles, title: "Curated Tools", body: "Hand-picked marketing tools — more launching soon." },
            { icon: ShieldCheck, title: "Admin Verified", body: "Manual approval keeps the circle safe and scam-free." },
            { icon: Zap, title: "Instant Access", body: "Approved members get login credentials in their dashboard immediately." },
          ].map((f, i) => (
            <div key={i} className="glass-card rounded-lg p-5">
              <div className="w-9 h-9 rounded-md bg-foreground/5 flex items-center justify-center mb-3">
                <f.icon className="w-4 h-4" />
              </div>
              <h3 className="font-heading font-bold text-lg">{f.title}</h3>
              <p className="text-sm text-muted-foreground mt-1">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="border-t border-border">
        <section id="plans" className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-muted-foreground mb-2">
              Pricing
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">
              Choose your plan
            </h2>
          </div>
          {!user && (
            <Link to="/signup">
              <Button variant="outline" data-testid="plans-signup">
                Sign Up to Purchase <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((p) => (
            <div
              key={p.id}
              className="glass-card rounded-lg p-6 flex flex-col"
              data-testid={`plan-card-${p.id}`}
            >
              <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">
                {p.tool_name}
              </p>
              <h3 className="font-heading font-bold text-xl mt-1">{p.name}</h3>
              <div className="flex items-baseline gap-1 mt-4">
                <span className="font-heading text-4xl font-black tracking-tight">
                  ৳{p.price}
                </span>
                <span className="text-sm text-muted-foreground">/ {p.duration_days}d</span>
              </div>
              <p className="text-sm text-muted-foreground mt-3 flex-1">{p.description}</p>
              <Link
                to={user ? `/account/plans?buy=${p.id}` : `/signup?next=/account/plans?buy=${p.id}`}
                className="mt-5"
              >
                <Button className="w-full" data-testid={`plan-buy-${p.id}`}>
                  {user ? "Buy Now" : "Sign Up to Buy"}
                </Button>
              </Link>
            </div>
          ))}
        </div>
        </section>
      </div>

      <div className="border-t border-border">
        <footer className="max-w-7xl mx-auto px-6 md:px-12 py-8 text-xs text-muted-foreground font-mono">
          © 2026 premiumtools.store · Premium AI tool access for Bangladesh.
        </footer>
      </div>
    </div>
  );
}
